import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ArrowLeft, Hash } from 'lucide-react';

const HashtagPage = ({ token, API, t, isRTL, tag, onBack, onOpenPost }) => {
  const [data, setData] = useState({ tag, posts_count: 0, posts: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/hashtags/${encodeURIComponent(tag)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data || { tag, posts_count: 0, posts: [] });
    } catch {
      setData({ tag, posts_count: 0, posts: [] });
    } finally {
      setLoading(false);
    }
  }, [API, token, tag]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="hashtag-page" dir={isRTL ? 'rtl' : 'ltr'} className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-30 bg-white border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={onBack}
            data-testid="hashtag-back"
            aria-label={t('back')}
            className="flex items-center gap-1 text-purple-600 font-medium p-2 -ms-2 min-h-[40px]"
          >
            <ArrowLeft aria-hidden="true" className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
            <span className="text-sm">{t('back')}</span>
          </button>
          <h1 className="text-base font-bold text-gray-900 truncate max-w-[60%] text-center">
            #{data.tag}
          </h1>
          <div className="w-12" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-3 py-4 pb-24">
        <section className="bg-white rounded-xl border border-gray-100 p-4 mb-4 flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center">
            <Hash className="w-6 h-6" />
          </div>
          <div>
            <p className="font-semibold text-gray-900">#{data.tag}</p>
            <p className="text-xs text-gray-500">
              {t('hashtagPostsCount', { n: data.posts_count })}
            </p>
          </div>
        </section>

        {loading ? (
          <div
            data-testid="hashtag-loading"
            role="status"
            aria-live="polite"
            aria-label={t('loadingContent')}
            className="grid grid-cols-3 gap-1"
          >
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="circleo-skeleton aspect-square rounded-none" />
            ))}
          </div>
        ) : data.posts.length === 0 ? (
          <p data-testid="hashtag-empty" className="text-center text-gray-500 py-10 text-sm">
            {t('noResultsFor', { q: '#' + data.tag })}
          </p>
        ) : (
          <div data-testid="hashtag-posts-grid" className="grid grid-cols-3 gap-1">
            {data.posts.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onOpenPost && onOpenPost(p)}
                data-testid={`hashtag-post-${p.id}`}
                className="aspect-square bg-gray-100 overflow-hidden"
              >
                {p.media_type === 'video' ? (
                  <video src={p.media_url} className="w-full h-full object-cover" />
                ) : (
                  <img src={p.media_url} alt="" className="w-full h-full object-cover" />
                )}
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default HashtagPage;
